/**
 * PDF Generator - Cotizaciones EXCTECLED (multi-pantalla)
 *
 * Estructura del PDF:
 *   Páginas promocionales : Imágenes estáticas (public/assets/proforma/)
 *   Para cada pantalla:
 *     · Ficha técnica   — pagina5_indoor.jpg o pagina5_outdoor.jpg
 *     · Visualización   — canvas grid + imágenes del producto
 *   Propuesta económica  — una sola página con todas las pantallas
 *   Datos de pago
 */

import { jsPDF } from 'jspdf';
import type { CotizacionDto } from '@/types/cotizacion';

// ── Dimensiones A4 en mm ──────────────────────────────────────────────────────
const PAGE_W = 210;
const PAGE_H = 297;

// Área de contenido sobre el fondo (deja espacio al header/footer del template)
const CX = 18;       // content left margin
const CY = 44;       // content top (debajo del header del fondo)
const CW = PAGE_W - CX - 18; // content width

// ── Paleta de colores EXCTECLED ───────────────────────────────────────────────
const DARK_BLUE: [number, number, number] = [15, 35, 60];
const DARK: [number, number, number] = [40, 40, 60];
const GRAY: [number, number, number] = [100, 100, 100];
const WHITE: [number, number, number] = [255, 255, 255];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

async function toBase64(url: string): Promise<string> {
  const res = await fetch(`${url}?v=${Date.now()}`);
  if (!res.ok) throw new Error(`No se pudo cargar imagen: ${url} (${res.status})`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function toArrayBuffer(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`No se pudo cargar fuente: ${url} (${res.status})`);
  return res.arrayBuffer();
}

/** Registra las variantes de BankGothic en la instancia jsPDF */
async function registerBankGothic(doc: jsPDF): Promise<void> {
  const [regular, bold, medium] = await Promise.all([
    toArrayBuffer('/assets/fonts/BankGothic-Regular.ttf'),
    toArrayBuffer('/assets/fonts/BankGothic-Bold.ttf'),
    toArrayBuffer('/assets/fonts/BankGothic-Medium.ttf'),
  ]);

  const toB64String = (buf: ArrayBuffer) => {
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  };

  doc.addFileToVFS('BankGothic-Regular.ttf', toB64String(regular));
  doc.addFont('BankGothic-Regular.ttf', 'BankGothic', 'normal');

  doc.addFileToVFS('BankGothic-Bold.ttf', toB64String(bold));
  doc.addFont('BankGothic-Bold.ttf', 'BankGothic', 'bold');

  doc.addFileToVFS('BankGothic-Medium.ttf', toB64String(medium));
  doc.addFont('BankGothic-Medium.ttf', 'BankGothic', 'medium');
}

function usd(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-PE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/** Obtiene las dimensiones naturales de una imagen base64 */
function getImageDimensions(base64: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = base64;
  });
}

/** Detecta el formato a partir del data URL */
function detectImgFormat(base64: string): string {
  if (base64.startsWith('data:image/png')) return 'PNG';
  if (base64.startsWith('data:image/webp')) return 'WEBP';
  return 'JPEG';
}

/**
 * Dibuja una imagen con fondo blanco manteniendo su aspecto (object-fit: contain).
 * boxX/boxY = esquina superior izquierda del box; boxW/boxH = tamaño del box.
 */
async function drawContainImage(
  doc: jsPDF,
  img: string,
  boxX: number,
  boxY: number,
  boxW: number,
  boxH: number,
) {
  const { w, h } = await getImageDimensions(img);
  const ratio = w / h;

  let drawW = boxW;
  let drawH = boxW / ratio;
  if (drawH > boxH) {
    drawH = boxH;
    drawW = boxH * ratio;
  }

  const offsetX = (boxW - drawW) / 2;
  const offsetY = (boxH - drawH) / 2;

  doc.addImage(img, detectImgFormat(img), boxX + offsetX, boxY + offsetY, drawW, drawH, undefined, 'FAST');
}

/**
 * Dibuja el grid de gabinetes en el PDF:
 * - Fondo del box completo: BLANCO
 * - imgfondo.jpg: solo dentro del área del grid (la pantalla)
 * - Líneas de gabinetes: blancas (sobre la imagen)
 * - Etiquetas exteriores (dimensiones, área, config): NEGRAS sobre el fondo blanco
 */
function drawPdfGrid(
  doc: jsPDF,
  canvasFondo: string,
  detalle: CotizacionDto['detalles'][0],
  boxX: number,
  boxY: number,
  boxW: number,
  boxH: number,
) {
  const { columnas, filas } = detalle;

  // Fondo blanco para toda el área del box
  doc.setFillColor(255, 255, 255);
  doc.rect(boxX, boxY, boxW, boxH, 'F');

  const padding = 12; // mm de margen interno (espacio para etiquetas)
  const totalWidthCm  = detalle.pantallaModular
    ? columnas * (detalle.rotado ? detalle.pantallaModular.altoModuloMm : detalle.pantallaModular.anchoModuloMm) / 10
    : columnas * detalle.producto!.anchoCm;
  const totalHeightCm = detalle.pantallaModular
    ? filas * (detalle.rotado ? detalle.pantallaModular.anchoModuloMm : detalle.pantallaModular.altoModuloMm) / 10
    : filas * detalle.producto!.altoCm;

  const scaleX = (boxW - padding * 2) / totalWidthCm;
  const scaleY = (boxH - padding * 2) / totalHeightCm;
  const scale  = Math.min(scaleX, scaleY);

  const gridW = totalWidthCm  * scale;
  const gridH = totalHeightCm * scale;

  const offsetX = boxX + (boxW - gridW) / 2;
  const offsetY = boxY + (boxH - gridH) / 2;

  const cellW = gridW / columnas;
  const cellH = gridH / filas;

  // imgfondo.jpg solo dentro del grid (la pantalla)
  doc.addImage(canvasFondo, 'JPEG', offsetX, offsetY, gridW, gridH, undefined, 'FAST');

  // Líneas de gabinetes — blancas sobre la imagen
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.3);
  for (let row = 0; row < filas; row++) {
    for (let col = 0; col < columnas; col++) {
      doc.rect(offsetX + col * cellW, offsetY + row * cellH, cellW, cellH, 'S');
    }
  }

  // Borde exterior más grueso
  doc.setLineWidth(0.8);
  doc.rect(offsetX, offsetY, gridW, gridH, 'S');

  // Etiquetas NEGRAS (fuera del grid, sobre fondo blanco)
  doc.setFont('BankGothic', 'bold');
  doc.setTextColor(0, 0, 0);

  // Ancho (arriba, centrado sobre el grid)
  const anchoM = (totalWidthCm / 100).toFixed(2);
  doc.setFontSize(10);
  doc.text(`${anchoM} m`, offsetX + gridW / 2, offsetY - 3, { align: 'center' });

  // Alto (izquierda pegado al grid, rotado)
  const altoM = (totalHeightCm / 100).toFixed(2);
  doc.text(`${altoM} m`, offsetX - 4, offsetY + gridH / 2, { angle: 90, align: 'center' });

  // Área (centro)
  // Área — dentro del grid, sobre la imagen → BLANCA
  const areaM2 = ((totalWidthCm * totalHeightCm) / 10000).toFixed(2);
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text(`${areaM2} m²`, offsetX + gridW / 2, offsetY + gridH / 2 + 2, { align: 'center' });

  // Config — fuera del grid, sobre fondo blanco → NEGRA
  doc.setFontSize(7);
  doc.setFont('BankGothic', 'normal');
  doc.setTextColor(0, 0, 0);
  const unidadLabel = detalle.pantallaModular ? 'módulos' : 'gabinetes';
  doc.text(
    `${columnas} × ${filas} = ${columnas * filas} ${unidadLabel}`,
    offsetX + gridW / 2,
    offsetY + gridH + 5,
    { align: 'center' },
  );
}

/** Calcula dimensiones totales de un detalle (gabinete o modular) */
function calcDimensions(detalle: CotizacionDto['detalles'][0]) {
  const { columnas, filas } = detalle;
  if (detalle.pantallaModular) {
    const pm = detalle.pantallaModular;
    const anchoMm = detalle.rotado ? pm.altoModuloMm : pm.anchoModuloMm;
    const altoMm  = detalle.rotado ? pm.anchoModuloMm : pm.altoModuloMm;
    const anchoTotalM = (columnas * anchoMm) / 1000;
    const altoTotalM = (filas * altoMm) / 1000;
    return { anchoTotalM, altoTotalM, areaM2: anchoTotalM * altoTotalM };
  }
  const p = detalle.producto!;
  const anchoTotalM = (columnas * p.anchoCm) / 100;
  const altoTotalM = (filas * p.altoCm) / 100;
  const areaM2 = anchoTotalM * altoTotalM;
  return { anchoTotalM, altoTotalM, areaM2 };
}

/** Parsea "320x160" → { h: 320, v: 160 } o null */
function parseResolucion(str: string): { h: number; v: number } | null {
  const m = str?.match(/^(\d+)[xX×](\d+)$/);
  if (!m) return null;
  return { h: parseInt(m[1]), v: parseInt(m[2]) };
}

/** Coloca el Pixel Pitch grande en la zona superior izquierda (área oscura).
 *  Aparece en páginas de ficha técnica y visualización.
 *  Acepta etiqueta opcional (p.ej. "Pantalla 1 de 3"). */
function addHeaderPixelPitch(doc: jsPDF, pixelPitch: number, label?: string) {
  doc.setFont('BankGothic', 'bold');
  doc.setFontSize(44);
  doc.setTextColor(...WHITE);
  doc.text(`P${pixelPitch}mm`, 18, 24);
  if (label) {
    doc.setFontSize(11);
    doc.setFont('BankGothic', 'normal');
    doc.text(label, 18, 32);
  }
}

/** Dibuja una página con fondo + pixel pitch en header */
function addBgPage(doc: jsPDF, fondo: string, pixelPitch: number, label?: string) {
  doc.addPage();
  doc.addImage(fondo, 'JPEG', 0, 0, PAGE_W, PAGE_H);
  addHeaderPixelPitch(doc, pixelPitch, label);
}

// ─────────────────────────────────────────────────────────────────────────────
// Sección: Ficha Técnica de una pantalla (página 5 indoor/outdoor)
// ─────────────────────────────────────────────────────────────────────────────

function renderFichaTecnica(
  doc: jsPDF,
  detalle: CotizacionDto['detalles'][0],
  pagina5Indoor: string,
  pagina5Outdoor: string,
  pantallaLabel: string,
) {
  const product = detalle.producto;
  const modular = detalle.pantallaModular;
  const pixelPitch = modular?.pixelPitch ?? product?.pixelPitch ?? 0;
  const isIndoor = (modular?.tipo ?? product?.tipo) === 'INDOOR';

  doc.addPage();
  doc.addImage(isIndoor ? pagina5Indoor : pagina5Outdoor, 'JPEG', 0, 0, PAGE_W, PAGE_H);
  addHeaderPixelPitch(doc, pixelPitch, pantallaLabel);

  const TECH_Y  = 115;
  const POWER_Y = 180.95;
  const OPT_Y   = 222;
  const ROW_H   = 6.3;
  const COL_L   = 28;
  const COL_R   = 110;
  const FS_P5   = 9;

  const writeRow = (label: string, value: string, y: number) => {
    doc.setFontSize(FS_P5);
    doc.setTextColor(...WHITE);
    doc.setFont('BankGothic', 'bold');
    doc.text(label, COL_L, y);
    doc.text(value, COL_R, y);
  };

  const dims = calcDimensions(detalle);
  const resTotal = product?.resolucionGabinete ? (() => {
    const r = parseResolucion(product.resolucionGabinete!);
    return r ? `${detalle.columnas * r.h} × ${detalle.filas * r.v} px` : '—';
  })() : '—';

  // — TECHNICAL SPECIFICATIONS —
  const techRows: [string, string][] = [];
  if (modular) {
    techRows.push(['Pixel Pitch', `${modular.pixelPitch} mm`]);
    if (modular.ledType)          techRows.push(['LED Encapsulation', modular.ledType]);
    if (modular.resolucionModulo) techRows.push(['Module Resolution', modular.resolucionModulo]);
    techRows.push(['Module Size', `${modular.anchoModuloMm} × ${modular.altoModuloMm} mm`]);
    techRows.push(['Size', `${dims.anchoTotalM.toFixed(2)} × ${dims.altoTotalM.toFixed(2)} m`]);
    techRows.push(['Configuración', `${detalle.columnas} col × ${detalle.filas} fil`]);
    if (modular.refreshRate) techRows.push(['High Refresh Rate', `${modular.refreshRate} Hz`]);
    if (modular.pesoModuloGramos) techRows.push(['Weight', `${modular.pesoModuloGramos} g/módulo`]);
  } else if (product) {
    if (product.pixelPitch)         techRows.push(['Pixel Pitch',      `${product.pixelPitch} mm`]);
    if (product.ledType)            techRows.push(['LED Encapsulation', product.ledType]);
    if (product.resolucionGabinete) techRows.push(['Pixel Density',    product.resolucionGabinete]);
    techRows.push(['Size', `${product.anchoCm} × ${product.altoCm} cm`]);
    if (product.resolucionGabinete) techRows.push(['Total Resolution',  resTotal]);
    techRows.push(['Configuración',     `${detalle.columnas} col × ${detalle.filas} fil`]);
    if (product.refreshRate)        techRows.push(['High Refresh Rate', `${product.refreshRate} Hz`]);
    if (product.pesoKg)             techRows.push(['Weight',            `${product.pesoKg} kg`]);
  }
  techRows.forEach(([lbl, val], i) => writeRow(lbl, val, TECH_Y + i * ROW_H));

  // — POWER SUPPLY —
  const spec = modular ?? product;
  const powerRows: [string, string][] = [];
  if (spec?.consumoMaxW)  powerRows.push(['Max. Power Consumption', `${spec.consumoMaxW} W/m²`]);
  if (spec?.consumoPromW) powerRows.push(['Avg. Power Consumption', `${spec.consumoPromW} W/m²`]);
  if (spec?.consumoPromW) powerRows.push(['Operation Power', '100-240V 50-60Hz']);
  if (spec?.consumoPromW) powerRows.push(['Connections', 'Power In/Out, Data In/Out']);
  powerRows.forEach(([lbl, val], i) => writeRow(lbl, val, POWER_Y + i * ROW_H));

  // — OPTICAL RATINGS —
  const optRows: [string, string][] = [];
  if (modular) {
    if (modular.brilloNits) optRows.push(['Brightness', `${modular.brilloNits} nits`]);
    if (modular.profundidadModuloMm) optRows.push(['Depth', `${modular.profundidadModuloMm} mm`]);
    if (modular.brilloNits) optRows.push(['Brightness Adjustment', '0-100% 100 levels']);
    optRows.push(['Frame frequency', '60Hz']);
    if (modular.brilloNits) optRows.push(['View Angle', 'Horizontal 140°, Vertical 140°']);
  } else if (product) {
    if (product.brilloNits)   optRows.push(['Brightness',    `${product.brilloNits} nits`]);
    if (product.proteccionIp) optRows.push(['Protection IP', product.proteccionIp]);
    if (product.vidaUtil)     optRows.push(['Lifespan',     `${product.vidaUtil.toLocaleString()} horas`]);
    if (product.brilloNits) optRows.push(['Depth',  `${product.profundidadMm} mm`]);
    if (product.brilloNits) optRows.push(['Brightness Adjustment',  '0-100% 100 levels']);
    if (product.brilloNits) optRows.push(['Frame frequency',  '60Hz']);
    if (product.brilloNits) optRows.push(['View Angle',  'Horizontal 140°, Vertical 140°']);
  }
  optRows.forEach(([lbl, val], i) => writeRow(lbl, val, OPT_Y + i * ROW_H));
}

// ─────────────────────────────────────────────────────────────────────────────
// Sección: Visualización (canvas + imágenes) de una pantalla
// ─────────────────────────────────────────────────────────────────────────────

async function renderVisualizacion(
  doc: jsPDF,
  detalle: CotizacionDto['detalles'][0],
  imgs: { cabinet: string | null; module: string | null },
  canvasFondo: string,
  fondo: string,
  pantallaLabel: string,
) {
  const pixelPitch = detalle.pantallaModular?.pixelPitch ?? detalle.producto?.pixelPitch ?? 0;
  addBgPage(doc, fondo, pixelPitch, pantallaLabel);

  const CANVAS_H = 110;
  const CANVAS_W = CW;
  const CANVAS_Y = 58;

  drawPdfGrid(doc, canvasFondo, detalle, CX, CANVAS_Y, CANVAS_W, CANVAS_H);

  const IMG_W  = 82;
  const IMG_H  = 90;
  const IMG_Y  = CANVAS_Y + CANVAS_H + 4;
  const GAP    = PAGE_W - CX - 18 - 2 * IMG_W;
  const CAB_X  = CX;
  const MOD_X  = CX + IMG_W + GAP;

  if (detalle.pantallaModular) {
    const SINGLE_W = 120;
    const SINGLE_X = CX + (CW - SINGLE_W) / 2;
    if (imgs.cabinet) await drawContainImage(doc, imgs.cabinet, SINGLE_X, IMG_Y, SINGLE_W, IMG_H);
  } else {
    if (imgs.cabinet) await drawContainImage(doc, imgs.cabinet, CAB_X, IMG_Y, IMG_W, IMG_H);
    if (imgs.module)  await drawContainImage(doc, imgs.module,  MOD_X, IMG_Y, IMG_W, IMG_H);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Función principal
// ─────────────────────────────────────────────────────────────────────────────

export async function generateCotizacionPdf(cotizacion: CotizacionDto): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  await registerBankGothic(doc);

  // Pixel pitch del primer producto/modular (para datos de pago / nombre archivo)
  const mainProduct = cotizacion.detalles[0]?.producto ?? null;
  const mainModular = cotizacion.detalles[0]?.pantallaModular ?? null;
  const headerPixelPitch = mainModular?.pixelPitch ?? mainProduct?.pixelPitch ?? 0;

  // Cargar imágenes estáticas en paralelo (ambas variantes de pagina5)
  const [p1, p3, p5_indoor, p5_outdoor, fondo, canvasFondo] = await Promise.all([
    toBase64('/assets/proforma/pagina1.jpg'),
    toBase64('/assets/proforma/pagina3.jpg'),
    toBase64('/assets/proforma/pagina5_indoor.jpg'),
    toBase64('/assets/proforma/pagina5_outdoor.jpg'),
    toBase64('/assets/proforma/paginafondo.jpg'),
    toBase64('/assets/canvasfondo.jpg'),
  ]);

  // Cargar imágenes de productos (una por detalle)
  const productImages = await Promise.all(
    cotizacion.detalles.map(async (d) => {
      if (d.pantallaModular) {
        const imgUrl = d.pantallaModular.imagen;
        const cabinet = imgUrl
          ? await toBase64(imgUrl.startsWith('http') ? imgUrl : `/uploads/${imgUrl}`).catch(() => null)
          : null;
        return { cabinet, module: null };
      }
      const p = d.producto!;
      return {
        cabinet: p.imagenCabinet
          ? await toBase64(p.imagenCabinet.startsWith('http') ? p.imagenCabinet : `/uploads/${p.imagenCabinet}`).catch(() => null)
          : null,
        module: p.imagenModule
          ? await toBase64(p.imagenModule.startsWith('http') ? p.imagenModule : `/uploads/${p.imagenModule}`).catch(() => null)
          : null,
      };
    })
  );

  // ── PÁGINAS promocionales ────────────────────────────────────────────────────
  [p1, p3].forEach((img, i) => {
    if (i > 0) doc.addPage();
    doc.addImage(img, 'JPEG', 0, 0, PAGE_W, PAGE_H);
  });

  // ── Por cada pantalla: ficha técnica + visualización ─────────────────────────
  const totalPantallas = cotizacion.detalles.length;
  for (let i = 0; i < cotizacion.detalles.length; i++) {
    const detalle = cotizacion.detalles[i];
    const label = totalPantallas > 1 ? `Pantalla ${i + 1} de ${totalPantallas}` : '';
    renderFichaTecnica(doc, detalle, p5_indoor, p5_outdoor, label);
    await renderVisualizacion(doc, detalle, productImages[i], canvasFondo, fondo, label);
  }

  // ── PROPUESTA ECONÓMICA ──────────────────────────────────────────────────────
  addBgPage(doc, fondo, headerPixelPitch);

  const RIGHT = PAGE_W - 18;
  const COL_PRICE = RIGHT - 38;
  const COL_QTY   = COL_PRICE - 28;
  const COL_TOTAL = RIGHT;
  const P7Y = CY + 15;

  // ── Encabezado: SEND TO (izq) | empresa (der) ────────────────────────────
  let py = P7Y;
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.setFont('BankGothic', 'normal');
  doc.text('ENVIAR A', CX, py);

  py += 6;
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.setFont('BankGothic', 'bold');
  doc.text(cotizacion.cliente.nombre, CX, py);

  py += 6;
  doc.setFont('BankGothic', 'normal');
  doc.setFontSize(9);
  if (cotizacion.cliente.dni) {
    const dniLabel = cotizacion.cliente.dni.length === 11 ? 'RUC' : 'DNI';
    doc.text(`${dniLabel}: ${cotizacion.cliente.dni}`, CX, py);
    py += 5;
  }
  if (cotizacion.cliente.rubro)   { doc.text(cotizacion.cliente.rubro,    CX, py); py += 5; }
  if (cotizacion.cliente.correo)  { doc.text(cotizacion.cliente.correo,   CX, py); py += 5; }
  if (cotizacion.cliente.telefono){ doc.text(cotizacion.cliente.telefono, CX, py); py += 5; }
  if (cotizacion.cliente.ciudad)  { doc.text(cotizacion.cliente.ciudad,   CX, py); py += 5; }
  if (cotizacion.cliente.representante) {
    doc.text(`Representante: ${cotizacion.cliente.representante}${cotizacion.cliente.cargoRepresentante ? ' — ' + cotizacion.cliente.cargoRepresentante : ''}`, CX, py);
    py += 5;
  }

  // Info empresa (columna derecha)
  doc.setFont('BankGothic', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...DARK_BLUE);
  doc.text('CHIPSHOW', RIGHT, P7Y + 6, { align: 'right' });
  doc.setFont('BankGothic', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  doc.text('pantallasledperu.com',                        RIGHT, P7Y + 13, { align: 'right' });
  if (cotizacion.usuarioTelefono) doc.text(cotizacion.usuarioTelefono, RIGHT, P7Y + 19, { align: 'right' });
  if (cotizacion.usuarioEmail)    doc.text(cotizacion.usuarioEmail,    RIGHT, P7Y + 25, { align: 'right' });

  // Número de cotización
  doc.setFont('BankGothic', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text(cotizacion.numeroCotizacion ?? '', RIGHT, P7Y + 33, { align: 'right' });

  // Separador
  const sepY = Math.max(py, P7Y + 37) + 4;
  doc.setDrawColor(180, 180, 190);
  doc.setLineWidth(0.2);
  doc.line(CX, sepY, RIGHT, sepY);

  let hy = sepY + 7;

  hy += 8;
  doc.setFont('BankGothic', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...DARK);
  const opLabel = cotizacion.tipoOperacion === 'VENTA' ? 'Compra' : 'Alquiler';
  doc.text(`Pantalla LED – ${opLabel}`, CX, hy);

  doc.setFont('BankGothic', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text(`Fecha: ${fmtDate(cotizacion.fechaCreacion)}`, RIGHT, hy, { align: 'right' });

  hy += 6;
  doc.setDrawColor(180, 180, 190);
  doc.line(CX, hy, RIGHT, hy);

  // Cabecera de columnas
  hy += 7;
  doc.setFont('BankGothic', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text('Ítem',      CX,        hy);
  doc.text('Precio',    COL_PRICE, hy, { align: 'right' });
  doc.text('Cantidad',  COL_QTY,   hy, { align: 'right' });
  doc.text('Total',     COL_TOTAL, hy, { align: 'right' });

  hy += 4;
  doc.setDrawColor(180, 180, 190);
  doc.line(CX, hy, RIGHT, hy);
  hy += 7;

  const PAGE_BREAK_Y = PAGE_H - 32;

  const addContentPage = () => {
    addBgPage(doc, fondo, headerPixelPitch);
    hy = P7Y;
    doc.setFont('BankGothic', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text('Ítem',      CX,        hy);
    doc.text('Precio',    COL_PRICE, hy, { align: 'right' });
    doc.text('Cantidad',  COL_QTY,   hy, { align: 'right' });
    doc.text('Total',     COL_TOTAL, hy, { align: 'right' });
    hy += 4;
    doc.setDrawColor(180, 180, 190);
    doc.line(CX, hy, RIGHT, hy);
    hy += 7;
  };

  // helper: fila de ítem
  const itemRow = (
    title: string,
    details: string[],
    price: string,
    qty: string,
    total: string,
  ) => {
    const TITLE_MAX_W = COL_QTY - CX - 8;
    doc.setFont('BankGothic', 'bold');
    doc.setFontSize(10);
    const titleLines: string[] = doc.splitTextToSize(title, TITLE_MAX_W);

    const estimatedH = 6 * titleLines.length + details.length * 5 + 6;
    if (hy + estimatedH > PAGE_BREAK_Y) addContentPage();

    doc.setTextColor(...DARK);
    doc.text(price, COL_PRICE, hy, { align: 'right' });
    doc.text(qty,   COL_QTY,   hy, { align: 'right' });
    doc.text(total, COL_TOTAL, hy, { align: 'right' });
    titleLines.forEach((line: string) => {
      if (hy > PAGE_BREAK_Y) addContentPage();
      doc.text(line, CX, hy);
      hy += 6;
    });
    doc.setFont('BankGothic', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    details.forEach((line) => {
      if (hy > PAGE_BREAK_Y) addContentPage();
      doc.text(`-${line}`, CX + 2, hy);
      hy += 5;
    });
    hy += 2;
    doc.setDrawColor(220, 220, 228);
    doc.setLineWidth(0.1);
    doc.line(CX, hy, RIGHT, hy);
    hy += 4;
  };

  // ── Pantallas cotizadas ───────────────────────────────────────────────────
  cotizacion.detalles.forEach((d, idx) => {
    const { anchoTotalM, altoTotalM, areaM2 } = calcDimensions(d);
    const cantidadPantallas = Math.max(1, d.cantidad ?? 1);
    const showMulti = totalPantallas > 1;
    const qtyText = cantidadPantallas > 1
      ? `${d.cantidadGabinetes} × ${cantidadPantallas}`
      : `${d.cantidadGabinetes}`;

    if (d.pantallaModular) {
      const pm = d.pantallaModular;
      const baseTitle = showMulti ? `Pantalla LED Modular #${idx + 1}` : 'Pantalla LED Modular';
      const title = cantidadPantallas > 1 ? `${baseTitle} (× ${cantidadPantallas})` : baseTitle;
      const details = [
        `${pm.nombre}`,
        `${d.columnas} × ${d.filas} = ${d.cantidadGabinetes} módulos`,
        `Dimensiones: ${anchoTotalM.toFixed(2)} m × ${altoTotalM.toFixed(2)} m  |  Área: ${areaM2.toFixed(2)} m²`,
        `Módulo: ${pm.anchoModuloMm}×${pm.altoModuloMm} mm  |  P${pm.pixelPitch}mm`,
      ];
      itemRow(
        title,
        details,
        usd(d.precioUnitario),
        qtyText,
        usd(d.subtotalPantalla),
      );
    } else {
      const p = d.producto!;
      const baseTitle = showMulti ? `Pantalla LED #${idx + 1}` : 'Pantalla LED';
      const title = cantidadPantallas > 1 ? `${baseTitle} (× ${cantidadPantallas})` : baseTitle;
      const details = [
        `${p.nombre}`,
        `${d.columnas} × ${d.filas} = ${d.cantidadGabinetes} gabinetes`,
        `Dimensiones: ${anchoTotalM.toFixed(2)} m × ${altoTotalM.toFixed(2)} m  |  Área: ${areaM2.toFixed(2)} m²`,
      ];
      if (p.resolucionGabinete) {
        const r = parseResolucion(p.resolucionGabinete);
        if (r) details.push(`Resolución total: ${d.columnas * r.h} × ${d.filas * r.v} px`);
      }
      itemRow(
        title,
        details,
        usd(d.precioUnitario),
        qtyText,
        usd(d.subtotalPantalla),
      );
    }
  });

  // ── Controlador / Procesador ─────────────────────────────────────────────
  if (cotizacion.controlador) {
    itemRow(
      cotizacion.controlador.nombre,
      [`Procesador de video — ${cotizacion.controlador.puertos} puertos ethernet`],
      usd(cotizacion.precioControlador),
      '1',
      usd(cotizacion.precioControlador),
    );
  }

  // ── Adicionales ──────────────────────────────────────────────────────────
  if (cotizacion.adicionales && cotizacion.adicionales.length > 0) {
    for (const adic of cotizacion.adicionales) {
      const cant = adic.cantidad ?? 1;
      itemRow(
        adic.descripcion,
        [],
        usd(adic.precio),
        `${cant}`,
        usd(adic.precio * cant),
      );
    }
  }

  // ── Cables (siempre $0.00) ────────────────────────────────────────────────
  itemRow(
    'Cables',
    [
      'Cables de energía principales',
      'Cables de datos principales',
      'Cables de energía y datos entre pantallas',
    ],
    '$0.00', '1', '$0.00',
  );

  // ── Delivery ─────────────────────────────────────────────────────────────
  const deliveryFree = cotizacion.ubicacion === 'LIMA';
  itemRow(
    `Envío (${deliveryFree ? 'Lima – incluido' : 'Provincia'})`,
    deliveryFree
      ? ['Entrega en Lima incluida']
      : ['Envío a provincia', 'Sujeto a ubicación final'],
    deliveryFree ? '$0.00' : usd(cotizacion.costoEnvio),
    '1',
    deliveryFree ? '$0.00' : usd(cotizacion.costoEnvio),
  );

  // ── Descuento ─────────────────────────────────────────────────────────────
  if (hy + 30 > PAGE_BREAK_Y) addContentPage();
  if (cotizacion.descuentoActivo && cotizacion.porcentajeDescuento > 0) {
    const sumaAdicionales = (cotizacion.adicionales ?? []).reduce((s, a) => s + a.precio * (a.cantidad ?? 1), 0);
    const baseDescuento = cotizacion.subtotal + (cotizacion.precioControlador || 0) + sumaAdicionales;
    const disc = baseDescuento * (cotizacion.porcentajeDescuento / 100);
    const discLabel = cotizacion.nombreDescuento
      ? `Descuento – ${cotizacion.nombreDescuento} (${cotizacion.porcentajeDescuento}%)`
      : `Descuento (${cotizacion.porcentajeDescuento}%)`;
    doc.setFont('BankGothic', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(180, 0, 0);
    doc.text(discLabel, CX, hy);
    doc.text(`-${usd(disc)}`, COL_TOTAL, hy, { align: 'right' });
    hy += 7;
  }

  // ── Separador + Total ─────────────────────────────────────────────────────
  doc.setDrawColor(180, 180, 190);
  doc.setLineWidth(0.2);
  doc.line(CX, hy, RIGHT, hy);
  hy += 6;

  doc.setFont('BankGothic', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text('Total:', COL_QTY - 10, hy, { align: 'right' });
  doc.setFontSize(14);
  doc.setTextColor(...DARK_BLUE);
  doc.text(usd(cotizacion.totalFinal), COL_TOTAL, hy, { align: 'right' });

  // ── Notas ────────────────────────────────────────────────────────────────
  if (cotizacion.notas) {
    hy += 12;
    doc.setFont('BankGothic', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text('NOTAS:', CX, hy);
    doc.setFont('BankGothic', 'normal');
    const lines = doc.splitTextToSize(cotizacion.notas, CW);
    doc.text(lines, CX, hy + 5);
  }

  // ── Página: Números de cuenta ─────────────────────────────────────────────
  addBgPage(doc, fondo, headerPixelPitch);

  const BX = CX;
  const BW = CW;
  let by = CY + 15;

  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.setFont('BankGothic', 'normal');
  doc.text('DATOS DE PAGO', BX, by);
  by += 6;

  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.setFont('BankGothic', 'bold');
  doc.text('EXCTEC LED S.A.C.', BX, by);
  by += 6;

  doc.setFont('BankGothic', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  doc.text('RUC: 20601127246', BX, by);
  by += 12;

  doc.setDrawColor(180, 180, 190);
  doc.setLineWidth(0.2);
  doc.line(BX, by, BX + BW, by);
  by += 10;

  const accounts = [
    { banco: 'BCP — SOLES (S/.)',             cuenta: '191 2395517 0 39',       cci: '002 191 002395517 03 956' },
    { banco: 'BCP — DÓLARES ($)',             cuenta: '191 2404263 1 94',       cci: '002 191 002404263 19 455' },
  ];

  accounts.forEach((acc, i) => {
    doc.setFont('BankGothic', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...DARK_BLUE);
    doc.text(acc.banco, BX, by);
    by += 5;

    doc.setFont('BankGothic', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(`Cuenta:  ${acc.cuenta}`, BX, by);
    by += 5;

    doc.text(`CCI:        ${acc.cci}`, BX, by);
    by += 8;

    if (i < accounts.length - 1) {
      doc.setDrawColor(180, 180, 190);
      doc.setLineWidth(0.2);
      doc.line(BX, by, BX + BW, by);
      by += 8;
    }
  });

  // ── Descarga ─────────────────────────────────────────────────────────────────
  const clientName = cotizacion.cliente.nombre.replace(/\s+/g, '_').slice(0, 30);
  const productCode = mainModular?.codigo ?? mainProduct?.codigo ?? 'cotizacion';
  doc.save(`${productCode}_${clientName}.pdf`);
}
